import gql from 'graphql-tag';
import AWSAppSyncClient from 'aws-appsync';
import {
  authenticateUserMutation,
  reauthenticateUserMutation,
  receiveScannedUIDSubscription,
  authAPIConfig,
  isValidBarcodeUserQuery,
  setUserBarcodePinMutation,
} from '../apis/AuthAPI';
import EventEmitter from 'events';


class NIFCOAuth extends EventEmitter {
  constructor(apiKey,appId,userType) {
    super();
    this._apiKey = apiKey;
    this._appId = appId;
    this._userType = userType;
    this._authInProcess = false;
    this._onReceiveRFIDData = this._onReceiveRFIDData.bind(this);
  }

  set userType(userType) {
    this._userType = userType;
  }

  get userType() {
    return this._userType;
  }

  async authenticateUser(username,authString) {
    const api = _getAPIClient(this._apiKey);
    const { data: { authenticateUser }, errors } = await api.mutate({
      mutation: gql(authenticateUserMutation),
      variables: {
        input: {
          username: username,
          appId: this._appId,
          authString: authString,
          userType: this._userType,
        },
      },
    });
    if(errors) throw new Error(JSON.stringify(errors));
    return authenticateUser;
  }

  async reauthenticateUser(username,sessionToken) {
    const api = _getAPIClient(this._apiKey);
    const { data: { reauthenticateUser }, errors } = await api.mutate({
      mutation: gql(reauthenticateUserMutation),
      variables: {
        input: {
          username: username,
          appId: this._appId,
          sessionToken: sessionToken,
          userType: this._userType,
        },
      },
    });
    if(errors) throw new Error(JSON.stringify(errors));
    return reauthenticateUser;
  }

  subscribeToRFIDReader(serial) {
    if(serial == null) return;
    if(this._RFIDReaderSub) this._RFIDReaderSub.unsubscribe();
    const api = _getAPIClient(this._apiKey);
    this._RFIDReaderSub = api.subscribe({
      query: gql(receiveScannedUIDSubscription),
      variables: { serial: serial },
    }).subscribe({
      next: this._onReceiveRFIDData,
      error: error => {
        this.emit('error',error);
        setTimeout(this.subscribeToRFIDReader, 5000);
      },
    });
  }

  async _onReceiveRFIDData({ data: { receiveScannedUID: { uid } } }) {
    try {
      // emit auth loading event
      this.emit('loading',true);

      // check if auth is already in process
      if(this._authInProcess) return;
      this._authInProcess = true;

      // validate input
      if(uid.length < 8) return;

      // authenticate user
      const authData = await this.authenticateUser(null,uid);
      if(authData == null || authData.credentials == null) {
        this.emit('failed');
        return;
      }

      // remove RFID uid subscription
      this.unsubscribeFromRFIDReader();

      // emit credentials object
      this.emit('credentials',{
        ...authData,
        userType: this._userType,
      });
    }
    catch(e) {
      console.error('handleReceivedUID error:',e);
      this.emit('failed');
    }
    finally {
      this._authInProcess = false;
      this.emit('loading',false);
    }
  }

  unsubscribeFromRFIDReader() {
    if(this._RFIDReaderSub) this._RFIDReaderSub.unsubscribe();
    this._RFIDReaderSub = null;
  }

  async validateBarcodeUser(barcode) {
    const api = _getAPIClient(this._apiKey);
    const { data: { isValidBarcodeUser }, errors } = await api.query({
      query: gql(isValidBarcodeUserQuery),
      variables: {
        barcode: barcode,
        userType: this._userType,
      },
    });
    if(errors) throw new Error(JSON.stringify(errors));
    return isValidBarcodeUser;
  }

  async setBarcodeUserPin(barcode,pin) {
    const api = _getAPIClient(this._apiKey);
    const { data: { setUserBarcodePin }, errors } = await api.mutate({
      mutation: gql(setUserBarcodePinMutation),
      variables: {
        barcode: barcode,
        userType: this._userType,
        pin: pin
      },
    });
    if(errors) throw new Error(JSON.stringify(errors));
    return setUserBarcodePin;
  }
}

const _getAPIClient = apiKey => new AWSAppSyncClient({
  url: authAPIConfig.aws_appsync_graphqlEndpoint,
  region: authAPIConfig.aws_appsync_region,
  disableOffline: true,
  auth: {
    type: authAPIConfig.aws_appsync_authenticationType,
    apiKey: apiKey
  }
},{
  defaultOptions: {
    mutation: {
      fetchPolicy: 'no-cache',
      errorPolicy: 'all'
    },
    query: {
      fetchPolicy: 'network-only',
      errorPolicy: 'all'
    }
  }
});

export default NIFCOAuth;
